/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { css, cva } from "@panda/css";
import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";

const carouselThumbnails = css({
	width: "100%",
});

const carouselThumbnailsContainer = css({
	alignItems: "center",
	display: "flex",
	flexWrap: "wrap",
	gap: "eds.dimension.16",
	justifyContent: "center",
	width: "100%",
});

const carouselThumb = cva({
	base: {
		aspectRatio: "16/9",
		borderRadius: "eds.borderRadius.8",
		cursor: "pointer",
		flexBasis: "min(20%, 7.5rem)",
		flexShrink: 0,
		height: "100%",
		outlineColor: "transparent",
		outlineStyle: "solid",
		outlineWidth: "0.125rem",
		overflow: "hidden",
		position: "relative",
		outlineOffset: "0.125rem",
		width: "100%",

		"&[data-active='true']": {
			outlineColor: "eds.border.focus",
		},

		"&:focus-visible": {
			outlineColor: "eds.border.focus",
		},

		"&:hover, &:focus-visible": {
			"& > *": {
				transform: "scale(1.05)",
			},
		},

		"& > *": {
			objectFit: "cover",
			inset: 0,
			position: "absolute",
			transition: "transform token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
			width: "100%",
			height: "100%",
		},

		"& .cosmos-attribution": {
			display: "none",
		},
	},
	variants: {
		selected: {
			true: {
				fontWeight: "bold",
			},
		},
	},
});

interface CosmosCarouselThumbnailsProps {
	emblaApi?: EmblaCarouselType;
	slides: ReactNode[];
}

export const CosmosCarouselThumbnails: FC<CosmosCarouselThumbnailsProps> = ({ emblaApi, slides }) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
		containScroll: "keepSnaps",
		dragFree: true,
	});

	const onThumbClick = useCallback(
		(index: number) => {
			if (!emblaApi || !emblaThumbsApi) return;
			emblaApi.scrollTo(index);
		},
		[emblaApi, emblaThumbsApi],
	);

	const onSelect = useCallback(
		(emblaApi: EmblaCarouselType) => {
			if (emblaThumbsApi) {
				setSelectedIndex(emblaApi.selectedScrollSnap());
				emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());
			}
		},
		[emblaThumbsApi],
	);

	useEffect(() => {
		if (!emblaApi) return;

		onSelect(emblaApi);
		emblaApi.on("select", onSelect).on("reInit", onSelect);
	}, [emblaApi, onSelect]);

	if (!slides?.length) return null;
	return (
		<div className={carouselThumbnails} ref={emblaThumbsRef}>
			<div className={carouselThumbnailsContainer}>
				{slides.map((slide, index) => (
					<button
						className={carouselThumb({ selected: index === selectedIndex })}
						data-active={index === selectedIndex}
						key={index}
						onClick={() => onThumbClick(index)}
						type="button"
					>
						<span>{index + 1}</span>
						<span aria-hidden="true">{slide}</span>
					</button>
				))}
			</div>
		</div>
	);
};
